import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Play } from 'lucide-react';
import NumberInput from '../../components/ui/NumberInput';

interface Schedule {
    id: string;
    jobName: string;
    frequency: string;
    time: string; // HH:mm
    dayOfWeek: number | null;
    dayOfMonth: number | null;
    isEnabled: boolean;
    lastRunAt: string | null;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

// System maintenance job (status transitions) — global, not tied to any gym and
// not a WhatsApp message job.
const MEMBERSHIP_STATUS_JOB = 'membership_status_transition';

export default function ScheduledJobsListTab({ token }: Props) {
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

    useEffect(() => {
        fetchSchedules();
    }, [token]);

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/saas/schedules`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch schedules');
            const data = await response.json();
            // Only system-maintenance jobs live here; WhatsApp message jobs stay
            // under Messaging → Schedules.
            setSchedules((data.data as Schedule[]).filter(s => s.jobName === MEMBERSHIP_STATUS_JOB));
        } catch (error) {
            console.error(error);
            toast.error('Failed to load scheduled jobs');
        } finally {
            setLoading(false);
        }
    };

    // System maintenance job — runs globally with no gym/member selection, so it
    // uses a dedicated endpoint.
    const runMembershipStatusNow = async () => {
        const t = toast.loading('Running membership status update…');
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/membership-status/run-now`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Failed');
            toast.success(`Done — ${data.data.activated} activated, ${data.data.expired} expired`, { id: t });
            fetchSchedules();
        } catch (e: any) {
            toast.error(e.message || 'Failed to run', { id: t });
        }
    };

    const handleToggle = async (id: string, currentStatus: boolean) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/saas/schedules/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isEnabled: !currentStatus })
            });
            if (!response.ok) throw new Error('Failed to update status');

            toast.success('Status updated');
            fetchSchedules();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleEditSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingSchedule) return;

        try {
            const { frequency, time, dayOfWeek, dayOfMonth } = editingSchedule;

            const response = await fetch(`${API_BASE_URL}/api/saas/schedules/${editingSchedule.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ frequency, time, dayOfWeek, dayOfMonth })
            });

            if (!response.ok) throw new Error('Failed to update schedule');

            toast.success('Schedule updated');
            setEditingSchedule(null);
            fetchSchedules();
        } catch (error) {
            toast.error('Failed to update schedule');
        }
    };

    if (loading) return <div className="p-4 text-center">Loading scheduled jobs...</div>;

    return (
        <div>
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {schedules.map((schedule) => (
                        <li key={schedule.id} className="px-6 py-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-indigo-600 uppercase tracking-wider">{schedule.jobName.replace(/_/g, ' ')}</h3>
                                <div className="mt-1 text-sm text-gray-500">
                                    <span className="font-medium text-gray-900">{schedule.frequency.toUpperCase()}</span> at <span className="font-medium text-gray-900">{schedule.time}</span>

                                    {schedule.frequency === 'weekly' && schedule.dayOfWeek !== null && (
                                        <span className="ml-1">on {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][schedule.dayOfWeek]}</span>
                                    )}
                                    {schedule.frequency === 'monthly' && schedule.dayOfMonth !== null && (
                                        <span className="ml-1">on Day {schedule.dayOfMonth}</span>
                                    )}
                                </div>
                                {schedule.lastRunAt && (
                                    <p className="text-xs text-gray-400 mt-1">Last run: {new Date(schedule.lastRunAt).toLocaleString()}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={runMembershipStatusNow}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                    <Play className="h-3.5 w-3.5" />
                                    Run Now
                                </button>
                                <button className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${schedule.isEnabled ? 'bg-green-600' : 'bg-gray-200'
                                    }`}
                                    onClick={() => handleToggle(schedule.id, schedule.isEnabled)}
                                >
                                    <span className="sr-only">Use setting</span>
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${schedule.isEnabled ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                                <button
                                    onClick={() => setEditingSchedule(schedule)}
                                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                                >
                                    Edit
                                </button>
                            </div>
                        </li>
                    ))}
                    {schedules.length === 0 && (
                        <li className="px-6 py-12 text-center text-sm text-gray-500">No scheduled jobs configured.</li>
                    )}
                </ul>
            </div>

            {/* Edit Modal */}
            {editingSchedule && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Schedule: {editingSchedule.jobName}</h3>
                        <form onSubmit={handleEditSave}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Frequency</label>
                                    <select
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                        value={editingSchedule.frequency}
                                        onChange={(e) => setEditingSchedule({ ...editingSchedule, frequency: e.target.value })}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Time (HH:MM)</label>
                                        <input
                                            type="time"
                                            required
                                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            value={editingSchedule.time}
                                            onChange={(e) => setEditingSchedule({ ...editingSchedule, time: e.target.value })}
                                        />
                                    </div>

                                    {editingSchedule.frequency === 'weekly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Day of Week</label>
                                            <select
                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                                value={editingSchedule.dayOfWeek || 0}
                                                onChange={(e) => setEditingSchedule({ ...editingSchedule, dayOfWeek: parseInt(e.target.value) })}
                                            >
                                                <option value={0}>Sunday</option>
                                                <option value={1}>Monday</option>
                                                <option value={2}>Tuesday</option>
                                                <option value={3}>Wednesday</option>
                                                <option value={4}>Thursday</option>
                                                <option value={5}>Friday</option>
                                                <option value={6}>Saturday</option>
                                            </select>
                                        </div>
                                    )}

                                    {editingSchedule.frequency === 'monthly' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Day of Month</label>
                                            <NumberInput
                                                integer
                                                min={1}
                                                max={31}
                                                emptyValue={1}
                                                className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                value={editingSchedule.dayOfMonth || 1}
                                                onChange={(v) => setEditingSchedule({ ...editingSchedule, dayOfMonth: v ?? 1 })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setEditingSchedule(null)}
                                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
